import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateStocktakeRequest,
    ListStocktakeParams,
    UpdateLineRequest,
} from '@/lib/api/stocktake-api'
import { stocktakeApi } from '@/lib/api/stocktake-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export const stocktakeKeys = {
    all: ['stocktake'] as const,
    lists: (params?: ListStocktakeParams) =>
        [...stocktakeKeys.all, 'list', params ?? {}] as const,
    detail: (id: string) => [...stocktakeKeys.all, 'detail', id] as const,
}

export function useStocktakeSessions(
    params?: ListStocktakeParams,
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: stocktakeKeys.lists(params),
        queryFn: async () => {
            const res = await stocktakeApi.list(params)
            return { items: res.data ?? [], meta: res.meta }
        },
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useStocktakeSessionsInfinite(
    params: Omit<ListStocktakeParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...stocktakeKeys.all, 'list', 'infinite', params] as const,
        queryFn: async ({ pageParam }) => {
            const res = await stocktakeApi.list({ ...params, page: pageParam })
            return { items: res.data ?? [], meta: res.meta }
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useStocktakeSession(id: string, enabled = true) {
    return useQuery({
        queryKey: stocktakeKeys.detail(id),
        queryFn: async () => {
            const res = await stocktakeApi.get(id)
            return res.data
        },
        enabled: enabled && !!id,
    })
}

export function useCreateStocktake() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateStocktakeRequest) => stocktakeApi.create(data),
        onSuccess: () => {
            toast.success(m.stocktake_create_success())
            void qc.invalidateQueries({ queryKey: stocktakeKeys.all })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.stocktake_create_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function useUpdateStocktakeLine(sessionId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (params: { lineId: string; data: UpdateLineRequest }) =>
            stocktakeApi.updateLine(sessionId, params.lineId, params.data),
        onSuccess: () => {
            void qc.invalidateQueries({
                queryKey: stocktakeKeys.detail(sessionId),
            })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.stocktake_update_line_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function useSubmitStocktake() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => stocktakeApi.submit(id),
        onSuccess: (_res, id) => {
            toast.success(m.stocktake_submit_success())
            void qc.invalidateQueries({ queryKey: stocktakeKeys.all })
            void qc.invalidateQueries({ queryKey: stocktakeKeys.detail(id) })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.stocktake_submit_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function useApproveStocktake() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => stocktakeApi.approve(id),
        onSuccess: (_res, id) => {
            toast.success(m.stocktake_approve_success())
            void qc.invalidateQueries({ queryKey: stocktakeKeys.all })
            void qc.invalidateQueries({
                queryKey: stocktakeKeys.detail(id),
            })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.stocktake_approve_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function useCancelStocktake() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => stocktakeApi.cancel(id),
        onSuccess: (_res, id) => {
            toast.success(m.stocktake_cancel_success())
            void qc.invalidateQueries({ queryKey: stocktakeKeys.all })
            void qc.invalidateQueries({ queryKey: stocktakeKeys.detail(id) })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.stocktake_cancel_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}
